/**
 * Emails and the Excel download. Every send starts with the email exactly as
 * it will go out and how many people it will reach; HR confirms from there.
 * While a send is still going out (the round's `sending_until` is ahead), the
 * send buttons wait and a Refresh lets HR watch the Invited count climb.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Bell, Download, FlaskConical, Loader2, Mail, RefreshCw, Send } from 'lucide-react'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { errorDetail, minutesUntil } from '@/lib/staffOrder'
import {
  staffOrderService,
  type StaffOrderEmailKind,
  type StaffOrderEmailPreview,
  type StaffOrderRoundDetail,
} from '@/services/api/staffOrderService'

import { EMAILS_PER_MINUTE, downloadBlob, formatCount, minutesLabel, minutesToSend } from './helpers'

type SendKey = 'testers' | 'invitations' | 'reminders'

const SENDS: Record<SendKey, { kind: StaffOrderEmailKind; testersOnly: boolean; label: string; title: string }> = {
  testers: { kind: 'invitation', testersOnly: true, label: 'Email testers', title: 'Email the testers' },
  invitations: { kind: 'invitation', testersOnly: false, label: 'Send invitations', title: 'Send invitations' },
  reminders: {
    kind: 'reminder',
    testersOnly: false,
    label: "Send reminder to people who haven't ordered",
    title: "Remind people who haven't ordered",
  },
}

export const TEST_SENDS_ONLY_NOTE = 'Only testers can be emailed outside the live platform.'

function reachText(key: SendKey, n: number): string {
  const who = `${formatCount(n)} ${n === 1 ? 'person' : 'people'}`
  if (key === 'testers') {
    return `This will email ${formatCount(n)} ${n === 1 ? 'tester who has' : 'testers who have'} not been invited yet.`
  }
  if (key === 'invitations') return `This will email ${who} who ${n === 1 ? 'has' : 'have'} not been invited yet.`
  return `This will email ${who} who ${n === 1 ? 'was' : 'were'} invited but ${n === 1 ? 'has' : 'have'} not ordered.`
}

interface EmailActionsProps {
  detail: StaffOrderRoundDetail
  /** Reload the round (counts, sending_until) without blanking the page. */
  onRefresh: () => Promise<void> | void
  refreshing?: boolean
}

export function EmailActions({ detail, onRefresh, refreshing = false }: EmailActionsProps) {
  const [now, setNow] = useState(() => new Date())
  const [loadingPreview, setLoadingPreview] = useState<SendKey | null>(null)
  const [preview, setPreview] = useState<{ key: SendKey; data: StaffOrderEmailPreview } | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [exporting, setExporting] = useState<'full' | 'procurement' | null>(null)
  const cancelRef = useRef<HTMLButtonElement | null>(null)

  const sendingUntil = detail.sending_until
  const minutesLeft = minutesUntil(sendingUntil, now)
  const emailsGoingOut = minutesLeft !== null

  // While emails go out, keep the minutes current, and look again once they should be done.
  useEffect(() => {
    setNow(new Date())
    const left = minutesUntil(sendingUntil)
    if (left === null || !sendingUntil) return
    const msLeft = new Date(sendingUntil).getTime() - Date.now()
    const tick = window.setInterval(() => setNow(new Date()), 30_000)
    const done = window.setTimeout(() => {
      setNow(new Date())
      void onRefresh()
    }, Math.max(0, msLeft) + 2_000)
    return () => {
      window.clearInterval(tick)
      window.clearTimeout(done)
    }
  }, [sendingUntil, onRefresh])

  const realSendBlocked: string | null = detail.test_sends_only
    ? TEST_SENDS_ONLY_NOTE
    : detail.status !== 'open'
      ? 'Open ordering before you email everyone.'
      : null

  const openPreview = async (key: SendKey) => {
    setLoadingPreview(key)
    setSendError(null)
    try {
      const data = await staffOrderService.getEmailPreview(detail.id, SENDS[key].kind, SENDS[key].testersOnly)
      setPreview({ key, data })
    } catch (err) {
      toast.error(errorDetail(err, 'The email could not be shown. Please try again.'))
    } finally {
      setLoadingPreview(null)
    }
  }

  const send = async () => {
    if (!preview || sending) return
    const spec = SENDS[preview.key]
    setSending(true)
    setSendError(null)
    try {
      const result =
        spec.kind === 'invitation'
          ? await staffOrderService.sendInvitations(detail.id, spec.testersOnly)
          : await staffOrderService.sendReminders(detail.id, spec.testersOnly)
      setPreview(null)
      toast.success(
        result.queued === 0
          ? 'There was nobody to email.'
          : `${formatCount(result.queued)} ${result.queued === 1 ? 'email is' : 'emails are'} on the way. They go out at about ${EMAILS_PER_MINUTE} a minute.`,
      )
      await onRefresh()
    } catch (err) {
      setSendError(errorDetail(err, 'The emails could not be sent. Please try again.'))
    } finally {
      setSending(false)
    }
  }

  const download = async (view: 'full' | 'procurement') => {
    setExporting(view)
    try {
      const { blob, filename } = await staffOrderService.exportOrders(detail.id, {
        includeTesters: false,
        procurement: view === 'procurement',
      })
      downloadBlob(blob, filename)
      toast.success(view === 'procurement' ? 'Excel for Procurement downloaded.' : 'Excel downloaded.')
    } catch (err) {
      toast.error(errorDetail(err, 'The Excel file could not be downloaded. Please try again.'))
    } finally {
      setExporting(null)
    }
  }

  const sendButton = (key: SendKey, icon: ReactNode, variant: 'default' | 'outline', blocked: boolean) => (
    <Button
      type="button"
      variant={variant}
      size="sm"
      onClick={() => openPreview(key)}
      disabled={emailsGoingOut || blocked || loadingPreview !== null}
    >
      {loadingPreview === key ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {SENDS[key].label}
    </Button>
  )

  const reach = preview ? (SENDS[preview.key].testersOnly ? preview.data.testers : preview.data.recipients) : 0

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-medium text-gray-700">Emails and download</h4>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => download('procurement')} disabled={exporting !== null}
              title="What to order and whom to hand it to: no job titles, levels or vouchers">
              {exporting === 'procurement' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Excel for Procurement
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => download('full')} disabled={exporting !== null}>
              {exporting === 'full' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Excel
            </Button>
          </div>
        </div>

        {emailsGoingOut && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" role="status">
            <span className="inline-flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              Emails are being sent — about {minutesLabel(minutesLeft as number)} left
            </span>
            <Button type="button" variant="ghost" size="sm" onClick={() => void onRefresh()} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {sendButton('testers', <FlaskConical className="h-4 w-4" />, 'outline', false)}
          {sendButton('invitations', <Send className="h-4 w-4" />, 'default', !!realSendBlocked)}
          {sendButton('reminders', <Bell className="h-4 w-4" />, 'default', !!realSendBlocked)}
        </div>
        {realSendBlocked && <p className="text-xs text-gray-500">{realSendBlocked}</p>}
        <p className="text-xs text-gray-500">
          Invitations go to people who have not been invited yet. Reminders go to people who were
          invited but have not ordered. The download leaves testers out.
        </p>
      </CardContent>

      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open && !sending) {
            setPreview(null)
            setSendError(null)
          }
        }}
      >
        <DialogContent
          className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden"
          // The preview iframe would otherwise take the first focus, and keys pressed
          // inside it (Escape included) never reach the dialog. Start on Cancel.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            cancelRef.current?.focus()
          }}
        >
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle>{SENDS[preview.key].title}</DialogTitle>
                <DialogDescription>
                  {reachText(preview.key, reach)}
                  {reach > 0 &&
                    ` They go out at about ${EMAILS_PER_MINUTE} a minute, so this takes about ${minutesLabel(minutesToSend(reach))}.`}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-shrink-0 rounded-md border bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-500">Subject:</span>{' '}
                <span className="font-medium">{preview.data.subject}</span>
              </div>
              <iframe
                title="Email preview"
                srcDoc={preview.data.html}
                sandbox=""
                className="min-h-[360px] w-full flex-1 rounded-md border bg-white"
              />
              {sendError && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                  {sendError}
                </div>
              )}
              {reach === 0 && <p className="text-sm text-gray-600">There is nobody to email right now.</p>}
              <DialogFooter className="flex-shrink-0 gap-2">
                <Button ref={cancelRef} type="button" variant="outline" onClick={() => setPreview(null)} disabled={sending}>
                  Cancel
                </Button>
                <Button type="button" onClick={send} disabled={sending || reach === 0}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {SENDS[preview.key].testersOnly
                    ? `Send to ${formatCount(reach)} ${reach === 1 ? 'tester' : 'testers'}`
                    : `Send to ${formatCount(reach)} ${reach === 1 ? 'person' : 'people'}`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  )
}
